// Écran « Financement » : ce que la flotte coûte encore.
//
// L'ENCOURS EST UNE ESTIMATION, ET L'ÉCRAN LE DIT. Loyer × mois restants +
// valeur résiduelle. Ce n'est pas le capital restant dû d'un tableau
// d'amortissement, qui demanderait le taux et la part d'intérêts de chaque
// échéance. Ce chiffre peut finir sur le bureau d'un banquier : présenté comme
// exact et faux de 8 %, il se retournerait contre le client.
//
// AUCUN CHAMP « ENCOURS » N'EST SAISISSABLE. Il se calcule côté serveur, comme
// le coût au kilomètre. Un chiffre qu'on peut retaper est un chiffre dont
// personne ne sait plus d'où il vient.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import api, { telecharger, chargerTousLesVehicules } from "../api";
import ComboBox from "../components/ComboBox";
import FinancementVehicule from "../components/FinancementVehicule";
import Layout from "../components/Layout";
import { SqueletteIndicateurs, SqueletteTableau } from "../components/Squelette";
import { ecartMoisJours, formaterDate } from "../format";
import { useModules } from "../modules";
import Notification from "../components/Notification";

// Les libellés vivent ici, les valeurs côté serveur (schemas.TypeContrat).
const CONTRATS = [
  { valeur: "credit_bail", label: "Crédit-bail" },
  { valeur: "loa", label: "LOA" },
  { valeur: "credit", label: "Crédit" },
  { valeur: "fonds_propres", label: "Fonds propres" },
];

const libelleContrat = (v) =>
  CONTRATS.find((c) => c.valeur === v)?.label || v;

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

/** Montant en euros. Un tiret quand la valeur manque — « 0 € » se lirait
 *  comme une dette soldée, ce qui n'est pas « on ne sait pas ». */
function euros(valeur) {
  if (valeur === null || valeur === undefined) return "—";
  return Number(valeur).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

/** Le délai avant la fin, en clair. C'est lui qui appelle une décision. */
function delai(jours, dateFin) {
  if (jours === null || jours === undefined) return { texte: "—", urgent: false };
  if (jours === 0) return { texte: "se termine aujourd’hui", urgent: true };
  // En mois et jours : « dans 609 j » obligeait à diviser de tête. L'urgence,
  // elle, reste jugée sur les jours du SERVEUR — le même seuil que l'e-mail.
  const ecart = ecartMoisJours(dateFin);
  const duree = ecart ? ecart.texte : `${Math.abs(jours)} j`;
  if (jours < 0) return { texte: `terminé depuis ${duree}`, urgent: true };
  // 90 jours : le préavis retenu pour l'alerte e-mail. Une fin de crédit-bail
  // demande de trouver un remplaçant et de décider du rachat de la VR.
  return { texte: `dans ${duree}`, urgent: jours <= 90 };
}

export default function Financement({ user, onLogout }) {
  const modules = useModules();

  const [lignes, setLignes] = useState(null);
  const [synthese, setSynthese] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [actifsSeuls, setActifsSeuls] = useState(true);
  const [exportEnCours, setExportEnCours] = useState("");

  // SAISIR UN CONTRAT DEPUIS CET ÉCRAN, sans passer par la fiche du véhicule.
  //
  // Avant, il fallait déjà savoir sur quel camion aller — donc connaître la
  // réponse avant de poser la question. Sur 108 véhicules, c'est un obstacle
  // suffisant pour que le contrat ne soit pas saisi du tout.
  const [vehicules, setVehicules] = useState([]);
  const [choix, setChoix] = useState("");

  // LE VÉHICULE SE DÉDUIT DE LA SAISIE, il ne se fige pas au moment du clic.
  //
  // Première version : on cherchait la correspondance DANS `onChange` et on
  // stockait le résultat. Un utilisateur qui tape (ou colle) une
  // immatriculation avant que la liste soit revenue du serveur ne trouvait
  // rien — et ne trouvait JAMAIS rien ensuite, puisque la recherche ne se
  // rejouait pas à l'arrivée de la liste. Le champ acceptait la saisie et
  // l'écran restait vide, sans un mot.
  //
  // Déduit à chaque rendu, le rapprochement se refait tout seul dès que la
  // liste arrive. Trouvé par le parcours navigateur, qui tape plus vite qu'un
  // humain — et c'est précisément pour ça qu'il vaut le coup.
  const vehiculeSaisie = useMemo(
    () => vehicules.find((v) => v.immatriculation === choix) || null,
    [vehicules, choix]
  );

  const charger = useCallback(() => {
    setChargement(true);
    Promise.all([
      api.get(`/financements?actifs_seuls=${actifsSeuls}`),
      api.get("/financements/synthese"),
    ])
      .then(([liste, s]) => {
        setLignes(liste.data);
        setSynthese(s.data);
      })
      .catch((err) =>
        setErreur(messageErreur(err, "Impossible de charger les financements."))
      )
      .finally(() => setChargement(false));
  }, [actifsSeuls]);

  useEffect(charger, [charger]);

  useEffect(() => {
    // Le sélecteur doit proposer TOUTE la flotte, sinon le camion cherché est
    // justement celui qui manque. « page_size: 500 » ne le garantissait pas :
    // 500 est le PLAFOND de la route, donc au-delà la liste était tronquée
    // sans un mot. `chargerTousLesVehicules` parcourt les pages.
    chargerTousLesVehicules()
      .then(setVehicules)
      .catch(() => {
        /* Le sélecteur reste vide ; l'encours, lui, s'affiche quand même. */
      });
  }, []);

  const exporter = (format) => {
    setExportEnCours(format);
    setErreur("");
    // Passe par telecharger() et non par un <a href> : la route est
    // authentifiée, et un lien direct ne porte pas le jeton — il répondrait
    // 401 sans rien dire.
    telecharger(
      `/financements/export.${format}?actifs_seuls=${actifsSeuls}`,
      `encours.${format}`
    )
      .then((nom) => {
        setMessage(`${nom} téléchargé.`);
        setTimeout(() => setMessage(""), 6000);
      })
      .catch((err) => setErreur(messageErreur(err, "Export impossible.")))
      .finally(() => setExportEnCours(""));
  };

  // Le module n'est pas souscrit : le serveur répondrait 403 sur chaque appel.
  // Ce n'est PAS la protection — c'est `exiger_module` — seulement une façon
  // de ne pas afficher un écran vide et incompréhensible.
  //
  // On renvoie vers la PRÉSENTATION du module et non vers le tableau de bord :
  // depuis le 12/08 le menu montre les modules non souscrits, et un clic qui
  // ramènerait à l'accueil sans un mot passerait pour un bug.
  if (modules.charge && !modules.module_financement)
    return <Navigate to="/modules/financement" replace />;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h1>Financement</h1>
        <div className="actions-groupe">
          <button
            className="btn"
            disabled={exportEnCours === "xlsx"}
            onClick={() => exporter("xlsx")}
          >
            {exportEnCours === "xlsx" ? "Export…" : "Exporter en Excel"}
          </button>
          <button
            className="btn"
            disabled={exportEnCours === "pdf"}
            onClick={() => exporter("pdf")}
          >
            {exportEnCours === "pdf" ? "Export…" : "Exporter en PDF"}
          </button>
        </div>
      </div>

      <Notification message={message} />
      {erreur && <p className="erreur">{erreur}</p>}

      <section className="carte">
        <div className="entete-section">
          <h3>Saisir un contrat</h3>
        </div>
        <div className="grille-form">
          <label className="champ">
            <span className="champ-label">Véhicule</span>
            <ComboBox
              value={choix}
              // Le bloc n'apparaît que sur une correspondance EXACTE : le
              // faire à chaque frappe le ferait clignoter et chargerait les
              // contrats d'un camion à demi saisi.
              onChange={setChoix}
              options={vehicules.map((v) => v.immatriculation)}
              placeholder="Chercher une immatriculation…"
            />
          </label>
        </div>

        {!vehiculeSaisie && (
          <p className="note-discrete">
            Choisissez un véhicule pour voir ses contrats et en saisir un
            nouveau.
          </p>
        )}
      </section>

      {/* ON RÉUTILISE LE BLOC DE LA FICHE VÉHICULE, on ne le recopie pas. Il
          porte le formulaire, ses bornes de saisie et ses aperçus calculés
          (date de fin déduite, VR en euros, TTC). Un second formulaire
          finirait par diverger du premier, et c'est la copie oubliée qui
          ferait le dégât — même raison que `_get_*_de_org` ou
          `stock.deplacer()` : une seule définition.

          Il est rendu EN FRÈRE et non dans la carte ci-dessus : le composant
          est lui-même une carte, et l'imbriquer ferait un cadre dans un
          cadre.

          `key` force un remontage au changement de véhicule — sans elle,
          React garderait l'état du formulaire précédent et on saisirait le
          contrat d'un camion dans celui d'un autre. */}
      {vehiculeSaisie && (
        <FinancementVehicule
          key={vehiculeSaisie.id}
          vehiculeId={vehiculeSaisie.id}
          estAdmin={user.role === "admin"}
          onModification={charger}
        />
      )}

      {chargement && !synthese ? (
        <SqueletteIndicateurs nombre={3} />
      ) : (
        synthese && (
          <div className="grille-indicateurs">
            <div className="carte-indicateur">
              <div className="indicateur-label">Encours estimé</div>
              <div className="indicateur-valeur">
                {euros(synthese.encours_total)}
              </div>
            </div>
            <div className="carte-indicateur">
              <div className="indicateur-label">Loyers mensuels</div>
              <div className="indicateur-valeur">
                {euros(synthese.loyer_mensuel_total)}
              </div>
            </div>
            <div
              className={
                "carte-indicateur" +
                (synthese.nb_fins_proches > 0 ? " carte-retard" : "")
              }
            >
              <div className="indicateur-label">Fins sous 90 jours</div>
              <div className="indicateur-valeur">{synthese.nb_fins_proches}</div>
            </div>
          </div>
        )
      )}

      {synthese && (
        <p className="sous-info">
          {synthese.mention}
          {synthese.nb_sans_encours_calculable > 0 && (
            <>
              {" "}
              <strong>
                {synthese.nb_sans_encours_calculable} contrat
                {synthese.nb_sans_encours_calculable > 1 ? "s" : ""} n’
                {synthese.nb_sans_encours_calculable > 1 ? "entrent" : "entre"} pas
                dans ce total
              </strong>{" "}
              — loyer ou date de fin non renseignés, ou achat sur fonds propres.
            </>
          )}
        </p>
      )}

      <div className="filtres">
        <label className="champ-interrupteur">
          <input
            type="checkbox"
            checked={!actifsSeuls}
            onChange={(e) => setActifsSeuls(!e.target.checked)}
          />
          <span>Afficher aussi les contrats terminés</span>
        </label>
      </div>

      {/* SqueletteTableau rend un <tbody> : il vit DANS le tableau, jamais à
          côté. L'en-tête reste donc affiché pendant le chargement — il ne
          dépend d'aucune donnée. */}
      {!chargement && lignes && lignes.length === 0 ? (
        <p className="vide">
          Aucun financement enregistré. Ils se saisissent depuis la fiche d’un
          véhicule.
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr>
              <th>Véhicule</th>
              <th>Contrat</th>
              <th>Organisme</th>
              <th>Fin</th>
              <th>Loyer HT</th>
              <th>Valeur résiduelle</th>
              <th>Encours estimé</th>
            </tr>
          </thead>
          {chargement && <SqueletteTableau lignes={6} colonnes={7} />}
          {!chargement && lignes && (
            <tbody>
              {lignes.map((l) => {
                const d = delai(l.jours_avant_fin, l.date_fin);
                return (
                  <tr key={l.id} className={d.urgent ? "urgence-rappel" : ""}>
                    <td>
                      <Link to={`/vehicules/${l.vehicule_id}`}>
                        {l.immatriculation || "—"}
                      </Link>
                      {!l.actif && (
                        <span className="note-discrete"> · terminé</span>
                      )}
                    </td>
                    <td>{libelleContrat(l.type_contrat)}</td>
                    <td>{l.organisme || "—"}</td>
                    <td>
                      {formaterDate(l.date_fin)}
                      <br />
                      <span
                        className={d.urgent ? "urgence-retard" : "note-discrete"}
                      >
                        {d.texte}
                      </span>
                    </td>
                    <td>{euros(l.loyer_mensuel)}</td>
                    {/* Le pourcentage est ce qui figure au contrat, les euros
                        sont ce qu'il faudra sortir pour racheter le camion.
                        Les deux, pour n'obliger personne au calcul de tête. */}
                    <td>
                      {l.valeur_residuelle_pct !== null &&
                      l.valeur_residuelle_pct !== undefined
                        ? `${Number(l.valeur_residuelle_pct)} %`
                        : "—"}
                      {l.valeur_residuelle_euros !== null &&
                        l.valeur_residuelle_euros !== undefined && (
                          <span className="note-discrete">
                            {" "}· {euros(l.valeur_residuelle_euros)}
                          </span>
                        )}
                    </td>
                    <td>
                      <strong>{euros(l.encours_estime)}</strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      )}
    </Layout>
  );
}
